import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, Drawer, IconButton, InputAdornment, MenuItem, Pagination, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { FiFilter } from 'react-icons/fi';
import { MdAdd, MdBusiness, MdCheckCircle, MdDeleteOutline, MdEdit, MdPauseCircle, MdSearch, MdVisibility } from 'react-icons/md';
import { deleteData, getData } from '../../Webservice/Webservice';
import { indianStates } from '../../Utils/states';
import SupplierForm from './SupplierForm';
import SupplierView from './SupplierView';
import { BUSINESS_TYPES, FONT, StatusChip, SupplierAvatar, authToken, typeLabel } from './supplierShared';

const LIMIT = 10;
const MORE = { panNumber: '', gstNumber: '', city: '', state: '', phone: '', contactPerson: '' };

const Kpi = ({ label, value, icon: Icon, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ cursor: 'pointer', flex: 1, minWidth: 180, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2, transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '12px', p: 1.1, display: 'flex' }}><Icon size={22} /></Box>
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
    </Box>
  </Box>
);

const iconBtn = (color) => ({ color, background: `${color}14`, borderRadius: '8px', '&:hover': { background: `${color}26` } });

function SupplierDetails() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [status, setStatus] = useState('');
  const [more, setMore] = useState(MORE);
  const [drawer, setDrawer] = useState(false);
  const [draft, setDraft] = useState(MORE);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const seq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const params = { page, limit: LIMIT, search: debounced, businessType, status, ...more };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('supplier/list', params, authToken(), false);
      if (mine !== seq.current) return;
      setRows(res?.suppliers || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setRows([]);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, businessType, status, more]);

  useEffect(() => { load(); }, [load]);

  const activeMore = Object.values(more).filter(Boolean).length;
  const pickStatus = (s) => { setStatus((cur) => (cur === s ? '' : s)); setPage(1); };

  const openEdit = (s) => { setViewing(null); setEditing(s); setFormOpen(true); };
  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteData(`supplier/delete/${toDelete._id}`, authToken());
      setToDelete(null);
      if (rows.length === 1 && page > 1) setPage(page - 1);
      else load();
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete the supplier');
    } finally {
      setDeleting(false);
    }
  };

  const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
  const cell = { fontFamily: FONT, fontSize: 13.5 };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Supplier management</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>The businesses you buy stock from.</Typography>
        </Box>
        <Button startIcon={<MdAdd />} variant="contained" onClick={() => { setEditing(null); setFormOpen(true); }} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}>Add supplier</Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi label="All suppliers" value={stats.total} icon={MdBusiness} from="#6366f1" to="#4338ca" active={!status} onClick={() => { setStatus(''); setPage(1); }} />
        <Kpi label="Active" value={stats.active} icon={MdCheckCircle} from="#22c55e" to="#15803d" active={status === 'active'} onClick={() => pickStatus('active')} />
        <Kpi label="Inactive" value={stats.inactive} icon={MdPauseCircle} from="#94a3b8" to="#475569" active={status === 'inactive'} onClick={() => pickStatus('inactive')} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search name, ID, phone, email, GST…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Business type" value={businessType} onChange={(e) => { setBusinessType(e.target.value); setPage(1); }} sx={{ minWidth: 170 }}>
          <MenuItem value="">All types</MenuItem>
          {BUSINESS_TYPES.map((b) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
        </TextField>
        <Button startIcon={<FiFilter />} onClick={() => { setDraft(more); setDrawer(true); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '10px', px: 2 }}>
          More filters{activeMore ? ` (${activeMore})` : ''}
        </Button>
        {(activeMore > 0 || businessType || status || search) && (
          <Button onClick={() => { setSearch(''); setBusinessType(''); setStatus(''); setMore(MORE); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>
        )}
      </Box>

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Supplier</TableCell>
                <TableCell sx={head}>Contact</TableCell>
                <TableCell sx={head}>Location</TableCell>
                <TableCell sx={head} align="center">Products</TableCell>
                <TableCell sx={head}>Status</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => (
                <TableRow key={i}>{[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No suppliers match these filters.</TableCell></TableRow>
              )}
              {!loading && rows.map((s) => (
                <TableRow key={s._id} hover>
                  <TableCell sx={cell}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <SupplierAvatar supplier={s} />
                      <Box>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{s.name}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{s.supplierId || '—'} · {typeLabel(s.businessType)}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={cell}>
                    <div>{s.contactPerson || '—'}</div>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{s.phone}{s.email ? ` · ${s.email}` : ''}</Typography>
                  </TableCell>
                  <TableCell sx={cell}>{[s.billingAddress?.city, s.billingAddress?.state].filter(Boolean).join(', ') || '—'}</TableCell>
                  <TableCell sx={cell} align="center">{(s.products || []).length}</TableCell>
                  <TableCell><StatusChip status={s.status} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View"><IconButton size="small" onClick={() => setViewing(s)} sx={iconBtn('#4f46e5')}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(s)} sx={iconBtn('#ca8a04')}><MdEdit size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteError(''); setToDelete(s); }} sx={iconBtn('#dc2626')}><MdDeleteOutline size={17} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} supplier{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 360 }, p: 3 } }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 2 }}>More filters</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[['contactPerson', 'Contact person'], ['phone', 'Phone'], ['panNumber', 'PAN'], ['gstNumber', 'GST number'], ['city', 'City']].map(([k, label]) => (
            <TextField key={k} size="small" label={label} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
          ))}
          <TextField select size="small" label="State" value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })}>
            <MenuItem value="">All states</MenuItem>
            {indianStates.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
          <Button fullWidth onClick={() => { setDraft(MORE); setMore(MORE); setPage(1); setDrawer(false); }} sx={{ textTransform: 'none', fontWeight: 700 }}>Reset</Button>
          <Button fullWidth variant="contained" onClick={() => { setMore(draft); setPage(1); setDrawer(false); }} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', '&:hover': { background: 'rgb(255 190 0)' } }}>Apply</Button>
        </Box>
      </Drawer>

      <SupplierForm open={formOpen} supplier={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />
      <SupplierView open={!!viewing} supplier={viewing} onClose={() => setViewing(null)} onEdit={openEdit} />

      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>Delete supplier?</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>
            <b>{toDelete?.name}</b> will be removed permanently. If you have received stock from them, mark them Inactive instead.
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SupplierDetails;
